import { HTTP_CODE_ERROR } from '@packages/toolbox/dist/constants/http-codes';
import { readAsDataUrl } from '@packages/toolbox/dist/utils/file-readers';
import { AxiosResponse, isAxiosError } from 'axios';
import { http } from 'SharedDir/backend/axios/http';

import { ATTACHMENT_ENDPOINTS } from 'SharedDir/backend/endpoints/attachments';
import { StatusResponse } from 'SharedDir/declarations/endpoint';
import {
  AttachmentType,
  FileFull,
  FileListFilters,
  FileUploadOptions,
  FileUsagesOut,
  SignatureData,
} from 'SharedDir/declarations/files';
import { Paginated, PaginationParams } from 'SharedDir/declarations/pagination';
import fileDownloader from 'SharedDir/utils/file-downloader';

export default class AttachmentsApiClass {
  protected endpoints = ATTACHMENT_ENDPOINTS;

  async downloadFile(fileId: string, save: boolean = true) {
    const data = await http<string>({
      ...this.endpoints.GET(fileId),
      responseType: 'blob',
    });

    if (save) fileDownloader.saveFileFromResponse(data);

    return data;
  }

  async getBase64Content(fileId: string) {
    return readAsDataUrl(await this.getBlobContent(fileId));
  }

  async getBlobContent(fileId: string) {
    const { data } = (await http<string>({
      ...this.endpoints.GET(fileId),
      responseType: 'blob',
    })) as unknown as AxiosResponse<Blob>;

    return data;
  }

  async removeFile(id: string) {
    const { data } = await http<StatusResponse>(this.endpoints.DELETE(id));
    return data;
  }

  async getInfo(id: string) {
    const { data } = await http<FileFull>(this.endpoints.GET_INFO(id));
    return data;
  }

  async getUsages(id: string) {
    const { data } = await http<FileUsagesOut>(this.endpoints.GET_USAGES(id));
    return data;
  }

  /**
   * Постраничный поиск файлов. Соответствует бэковому
   * `schemas/files.py:FileListParams` (camelCase aliases).
   * Для ROOT возвращаются все файлы; для прочих — только свои/профильные.
   */
  async getPaginated(params: PaginationParams<FileListFilters>) {
    const { data } = (await http({
      ...this.endpoints.LIST,
      params,
    })) as AxiosResponse<Paginated<FileFull>>;

    return data;
  }

  async updateWithContent(id: string, payload: FormData) {
    const { data } = (await http({
      ...this.endpoints.UPDATE_INFO_WITH_FILE(id),
      data: payload,
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    })) as AxiosResponse<FileFull>;

    return data;
  }

  /**
   * Алиас под `SimpleApiInterface.delete`, чтобы класс можно было
   * передать в `useList(useFilters, AttachmentsApi)` без обёрток.
   */
  async delete(id: string) {
    return this.removeFile(id);
  }

  /**
   * С помощью этого метода возможно загрузить только 1 файл!!
   * @param {FileList} files
   * @param options
   * @returns {Promise<any>}
   */
  async uploadFile(files: FileList, options: FileUploadOptions = {}) {
    if (files.length > 1) {
      throw new Error('Этот метод позволяет загружать только 1 файл!');
    }
    const formData = new FormData();
    // Поскольку JS API для загрузки файлов возвращает FileList даже если мы вырали 1 файл
    // Приходиться итерироваться по этому списку
    Array.from(files).forEach((file) => {
      formData.append('file', file);
    });
    Object.entries(options).forEach(([name, value]) => {
      if (value === null) {
        return;
      }
      formData.append(name, String(value));
    });
    return http<AttachmentType>({
      ...this.endpoints.UPLOAD,
      headers: {
        'Content-Type': 'multipart/form-data',
      },
      data: formData,
    });
  }

  async sign(id: string, signData: SignatureData) {
    const { data } = await http({
      ...this.endpoints.SIGN(id),
      data: signData,
    });
    return data;
  }

  async downloadSignature(fileId: string) {
    const data: AxiosResponse = await http({
      ...this.endpoints.GET_SIGNATURE(fileId),
      responseType: 'blob',
    });

    fileDownloader.saveFileFromResponse(data);

    return data;
  }

  /**
   * PKCS#7 без save-as. 404 — подписи нет.
   */
  async getSignatureContent(fileId: string): Promise<string | null> {
    try {
      const { data } = await http<Blob>({
        ...this.endpoints.GET_SIGNATURE(fileId),
        responseType: 'blob',
      });
      if (!data) {
        return null;
      }
      if (typeof data === 'string') {
        return data || null;
      }
      if (data instanceof Blob) {
        return await data.text();
      }
      return String(data);
    } catch (error) {
      const status = isAxiosError(error) ? (error.response?.status ?? error.status) : undefined;
      if (status === HTTP_CODE_ERROR.NOT_FOUND) {
        return null;
      }
      throw error;
    }
  }
}
